import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDMY } from "@/lib/date";
import { ChevronLeft, Plus, Printer, Shuffle, ArrowDownAZ, Hash, Lock, LockOpen, CheckCircle2, AlertTriangle } from "lucide-react";

import { KeyboardHelp } from "@/components/liveentry/KeyboardHelp";
import { EntryRow } from "@/components/liveentry/EntryRow";
import { ImportEntriesButton } from "@/components/liveentry/ImportEntriesButton";
import { useLiveEntryRows } from "@/lib/useLiveEntryRows";
import { blankRow } from "@/lib/liveentry-helpers";

// Hydrate a server-side entry into the row shape the grid uses. Pulled out so
// the initial-load and post-import refresh paths can share one mapping.
const entryToRow = (r) => ({
  _key: r.id,
  id: r.id,
  photographer_id: r.photographer_id,
  photographer_code: r.photographer_code || "",
  photographer_name: r.photographer_name || "",
  image_title: r.image_title || "",
  score: r.score == null ? "" : r.score,
  placement: r.placement,
  status: "saved",
  error: "",
  duplicateWarn: false,
});

export default function LiveEntry() {
  const { competitionId } = useParams();
  const { user } = useAuth();
  const canEdit = ["ADMIN", "SECRETARY"].includes(user?.role);

  const [comp, setComp] = useState(null);
  const [photogs, setPhotogs] = useState([]);
  const [loadErr, setLoadErr] = useState("");

  const {
    rows,
    setRows,
    updateRow,
    removeRow,
    addRow,
    addAllPhotographers,
    randomiseRows,
    sortRowsAlpha,
    sortRowsByCode,
    saveRow,
  } = useLiveEntryRows(comp, canEdit, photogs);

  // Track the lock state locally so we can update it optimistically when the
  // admin toggles it. Falls back to false while the competition is loading.
  const locked = !!comp?.order_locked;
  const toggleLock = async () => {
    if (!comp) return;
    const next = !locked;
    try {
      const { data } = await api.post(
        `/competitions/${comp.id}/lock?locked=${next}`,
      );
      setComp((c) => ({ ...c, ...data }));
      toast.success(next
        ? "Running order locked — reordering is disabled"
        : "Running order unlocked — reordering re-enabled");
    } catch (ex) {
      toast.error(`Couldn't ${next ? "lock" : "unlock"}: ${formatApiError(ex.response?.data?.detail) || ex.message}`);
    }
  };

  const photogByCode = useMemo(() => {
    const m = {};
    photogs.forEach((p) => { m[p.photographer_code.toUpperCase()] = p; });
    return m;
  }, [photogs]);

  // Initial load — competition meta, roster, and any existing entries.
  useEffect(() => {
    (async () => {
      try {
        const [c, p, e] = await Promise.all([
          api.get(`/competitions/${competitionId}`),
          api.get(`/photographers`, { params: { active_only: true } }),
          api.get(`/entries`, { params: { competition_id: competitionId } }),
        ]);
        setComp(c.data);
        setPhotogs(p.data);
        if (e.data.length) {
          setRows([...e.data.map(entryToRow), blankRow()]);
        }
      } catch (ex) {
        setLoadErr(formatApiError(ex.response?.data?.detail) || ex.message);
      }
    })();
    // setRows is stable; deliberately not in deps to avoid re-running on every render.
  }, [competitionId]);

  if (loadErr) return <div className="p-8 danger-text">{loadErr}</div>;
  if (!comp) return <div className="p-8 muted-text">Loading…</div>;

  const totalSaved = rows.filter((r) => r.status === "saved").length;

  // Publishing checklist — pre-flight signals to catch omissions before the
  // secretary hits Print/Publish. Blank trailing rows (no photographer) are
  // ignored so a fresh grid doesn't show "1 row unsaved".
  const dataRows = rows.filter((r) => r.photographer_id || r.photographer_code);
  const unsavedRows = dataRows.filter((r) => r.status !== "saved").length;
  const missingScoreRows = dataRows.filter(
    (r) => r.status === "saved" && (r.score === "" || r.score == null),
  ).length;
  const topPlacementSet = new Set(["FIRST", "SECOND", "THIRD"]);
  const topPlacementsAssigned = dataRows.filter(
    (r) => r.status === "saved" && topPlacementSet.has(r.placement),
  ).length;
  const hasScored = dataRows.some(
    (r) => r.status === "saved" && r.score !== "" && r.score != null,
  );
  const checklistReady =
    dataRows.length > 0 &&
    unsavedRows === 0 &&
    missingScoreRows === 0 &&
    topPlacementsAssigned >= 3;

  return (
    <div className="p-8 max-w-[1400px]">
      <Link to="/competitions" className="inline-flex items-center gap-1 muted-text text-sm hover:text-white mb-4">
        <ChevronLeft size={14} /> Competitions
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="ccs-label mb-2">Live Entry</div>
          <h1 className="text-3xl font-semibold tracking-tight">{comp.competition_name}</h1>
          <div className="text-sm muted-text mt-1 mono">
            {formatDMY(comp.competition_date)} · {comp.competition_type} · {comp.category}
            {comp.judge_name ? ` · Judge: ${comp.judge_name}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="ccs-label">Saved</div>
            <div className="mono text-2xl font-semibold success-text">{totalSaved}</div>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href={`/print/scoresheet/${comp.id}`}
              target="_blank" rel="noreferrer"
              className="ccs-btn-ghost flex items-center gap-2 text-sm"
              data-testid="print-scoresheet-button"
            >
              <Printer size={14} /> Print sheet
            </a>
            <a
              href={`/print/results/${comp.id}`}
              target="_blank" rel="noreferrer"
              className="ccs-btn-ghost flex items-center gap-2 text-sm"
              data-testid="print-results-button"
            >
              <Printer size={14} /> Print results
            </a>
          </div>
          {canEdit && (
            <ImportEntriesButton
              competitionId={comp.id}
              onImported={async () => {
                const e = await api.get(`/entries`, { params: { competition_id: comp.id } });
                setRows([...e.data.map(entryToRow), blankRow()]);
              }}
            />
          )}
        </div>
      </div>

      <KeyboardHelp />

      {dataRows.length > 0 && hasScored && (
        <div
          className={`mb-4 px-4 py-3 rounded flex items-start gap-3 text-sm ${
            checklistReady
              ? "border border-emerald-500/40 bg-emerald-500/5"
              : "border border-amber-500/40 bg-amber-500/5"
          }`}
          data-testid="publishing-checklist"
        >
          <div className="mt-0.5">
            {checklistReady ? (
              <CheckCircle2 size={16} className="text-emerald-400" />
            ) : (
              <AlertTriangle size={16} className="text-amber-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.25em] muted-text mb-1">
              Publishing checklist
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span
                className={unsavedRows > 0 ? "text-amber-300" : "muted-text"}
                data-testid="checklist-unsaved"
              >
                <span className="mono font-semibold">{unsavedRows}</span> row{unsavedRows === 1 ? "" : "s"} unsaved
              </span>
              <span className="muted-text">·</span>
              <span
                className={missingScoreRows > 0 ? "text-amber-300" : "muted-text"}
                data-testid="checklist-missing-scores"
              >
                <span className="mono font-semibold">{missingScoreRows}</span> row{missingScoreRows === 1 ? "" : "s"} missing a score
              </span>
              <span className="muted-text">·</span>
              <span
                className={topPlacementsAssigned < 3 ? "text-amber-300" : "muted-text"}
                data-testid="checklist-top3"
              >
                <span className="mono font-semibold">{topPlacementsAssigned}</span> of 3 top placements assigned
              </span>
              {checklistReady ? (
                <span className="ml-auto text-emerald-300 mono text-xs uppercase tracking-[0.2em]">
                  Ready to publish
                </span>
              ) : (
                <span className="ml-auto muted-text text-xs">
                  Review before printing / publishing
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="entry-grid" data-testid="entry-grid">
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <th style={{ width: 90, textAlign: "center" }}>I.D.</th>
              <th style={{ width: 180 }}>Photographer</th>
              <th>Image Title</th>
              <th style={{ width: 80 }}>Score</th>
              <th style={{ width: 90 }}>Place</th>
              <th style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <EntryRow
                key={r._key}
                index={idx}
                row={r}
                photogs={photogs}
                photogByCode={photogByCode}
                comp={comp}
                canEdit={canEdit}
                onChange={(patch) => updateRow(r._key, patch)}
                onSave={(overrides) => saveRow({ ...r, ...(overrides || {}) })}
                onRemove={() => removeRow(r)}
                onAdvance={() => {
                  if (idx === rows.length - 1) addRow();
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {canEdit && (
          <>
            <button
              onClick={addRow}
              disabled={locked}
              className="ccs-btn-ghost flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="add-row-button"
              title={locked ? "Unlock the running order first" : "Append a blank row to the grid"}
            >
              <Plus size={16} /> Add row
            </button>
            <button
              onClick={addAllPhotographers}
              disabled={locked}
              className="ccs-btn-ghost flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="add-all-photographers-button"
              title={locked ? "Unlock the running order first" : "Append a row for every active photographer; merges with existing rows sorted by I.D."}
            >
              <Plus size={16} /> Add all photographers
            </button>
            <button
              onClick={randomiseRows}
              disabled={locked}
              className="ccs-btn-ghost flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="randomise-button"
              title={locked ? "Unlock the running order first" : "Shuffle rows so duplicate photographers are spread across the running order"}
            >
              <Shuffle size={16} /> Randomise order
            </button>
            <button
              onClick={sortRowsByCode}
              disabled={locked}
              className="ccs-btn-ghost flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="sort-code-button"
              title={locked ? "Unlock the running order first" : "Sort rows by photographer I.D. (numeric-aware: 2, 3, 10, A1)"}
            >
              <Hash size={16} /> Sort by I.D.
            </button>
            <button
              onClick={sortRowsAlpha}
              disabled={locked}
              className="ccs-btn-ghost flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="sort-alpha-button"
              title={locked ? "Unlock the running order first" : "Sort rows alphabetically by photographer name"}
            >
              <ArrowDownAZ size={16} /> Sort A → Z
            </button>
            <button
              onClick={toggleLock}
              className={`ccs-btn-ghost flex items-center gap-2 ${locked ? "text-amber-400 border-amber-400/50" : ""}`}
              data-testid="lock-order-button"
              title={locked
                ? "Running order is locked — click to unlock and allow reordering"
                : "Lock the running order so it can't be reshuffled"}
            >
              {locked ? <><Lock size={16} /> Order locked</> : <><LockOpen size={16} /> Lock order</>}
            </button>
          </>
        )}
        <span className="muted-text text-xs">
          Tip: Press <span className="kbd">Enter</span> in a score to save & jump to the next score. Use <span className="kbd">Tab</span> to skip through Title → Score.
        </span>
      </div>
    </div>
  );
}
